import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaArrowDown, FaArrowUp, FaEdit, FaPlus, FaProjectDiagram, FaSave, FaTrash } from 'react-icons/fa';
import { approvalFlowAPI, rolesAPI, usersAPI } from '../services/api';
import { Modal, ModalFooter } from '../components/Modals';
import { invalidateApprovalFlows } from '../hooks/useApprovalRights';

// Settings → Approval Flow (LMS-style): for every activity, who must approve
// it, level by level. Each level lists the role(s) and/or named users who may
// sign it; any one of them signs for the level. Alerts follow the same levels.
const FlowEditor = ({ flow, roles, users, onClose, onSaved }) => {
  const [levels, setLevels] = useState(() => flow.levels.map((l) => ({ level_label: l.level_label, roles: [...l.roles], user_ids: [...l.user_ids] })));
  const [enabled, setEnabled] = useState(flow.enabled);
  const [allowSelf, setAllowSelf] = useState(flow.allow_self_approval);
  const [saving, setSaving] = useState(false);
  const fixed = flow.fixed_levels;

  const update = (i, patch) => setLevels((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const move = (i, d) => setLevels((ls) => {
    const next = [...ls];
    const j = i + d;
    if (j < 0 || j >= next.length) return ls;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const save = async () => {
    const empty = levels.findIndex((l) => !l.roles.length && !l.user_ids.length);
    if (empty >= 0) { toast.error(`Level ${empty + 1} needs at least one role or user`); return; }
    setSaving(true);
    try {
      await approvalFlowAPI.save(flow.workflow_type, { levels, enabled, allow_self_approval: allowSelf });
      invalidateApprovalFlows();
      toast.success(`${flow.label} approval flow saved`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen size="lg" title={`Approval Flow — ${flow.label}`} onClose={onClose}>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Approval required
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title="Maker-checker: when off, the person who created a record cannot approve it">
          <input type="checkbox" checked={allowSelf} onChange={(e) => setAllowSelf(e.target.checked)} /> Allow creator to approve own record
        </label>
      </div>
      <p className="text-muted" style={{ marginTop: 0, fontSize: 'var(--fs-sm)' }}>
        Records go through the levels in order. Any one user holding a listed role, or any named user, signs for a level. The same person cannot sign two levels of one record. Super Admin can always sign.
        {fixed ? ` ${flow.label} uses exactly ${fixed} levels: the Product stage first, then the QA stage.` : ''}
      </p>

      <div style={{ display: 'grid', gap: '10px' }}>
        {levels.map((lvl, i) => (
          <div key={i} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span className="badge badge-info">Level {i + 1}</span>
              <input className="form-control" style={{ flex: 1 }} value={lvl.level_label} onChange={(e) => update(i, { level_label: e.target.value })} placeholder="e.g. Department Approval, QA Approval" />
              {!fixed && (
                <>
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"><FaArrowUp /></button>
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => move(i, 1)} disabled={i === levels.length - 1} title="Move down"><FaArrowDown /></button>
                  <button type="button" className="btn btn-sm btn-danger-outline" onClick={() => setLevels((ls) => ls.filter((_, idx) => idx !== i))} disabled={levels.length === 1} title="Remove level"><FaTrash /></button>
                </>
              )}
            </div>
            <div className="form-label" style={{ marginBottom: '4px' }}>Roles</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: '8px' }}>
              {roles.map((r) => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-md)' }}>
                  <input type="checkbox" checked={lvl.roles.includes(r)} onChange={() => update(i, { roles: toggle(lvl.roles, r) })} /> {r}
                </label>
              ))}
            </div>
            <div className="form-label" style={{ marginBottom: '4px' }}>Named users (optional)</div>
            <select
              multiple
              className="form-control"
              style={{ minHeight: '72px' }}
              value={lvl.user_ids.map(String)}
              onChange={(e) => update(i, { user_ids: Array.from(e.target.selectedOptions).map((o) => Number(o.value)) })}
            >
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.employee_name || u.username} ({u.role})</option>)}
            </select>
            <small className="text-muted">Hold Ctrl to select several users.</small>
          </div>
        ))}
      </div>
      {!fixed && (
        <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: '10px' }} onClick={() => setLevels((ls) => [...ls, { level_label: `Level ${ls.length + 1}`, roles: [], user_ids: [] }])}>
          <FaPlus /> Add level
        </button>
      )}
      <ModalFooter>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}><FaSave /> {saving ? 'Saving…' : 'Save flow'}</button>
      </ModalFooter>
    </Modal>
  );
};

const ApprovalFlow = () => {
  const [flows, setFlows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const userNames = useMemo(() => Object.fromEntries(users.map((u) => [u.user_id, u.employee_name || u.username])), [users]);

  const load = useCallback(async () => {
    try {
      setFlows((await approvalFlowAPI.getAll()).data.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load approval flows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    rolesAPI.getAll().then((r) => setRoles((r.data.data || []).filter((x) => x.is_active !== false).map((x) => x.role_name))).catch(() => {});
    usersAPI.getAll().then((r) => setUsers((r.data.data || r.data || []).filter((u) => u.is_active !== false))).catch(() => {});
  }, [load]);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  const groups = [...new Set(flows.map((f) => f.group))];

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FaProjectDiagram /> Approval Flow</h2>
        <p className="page-subtitle">Choose, for every activity, who must approve it, level by level. Approval buttons, e-signatures and alerts all follow these levels.</p>
      </div>
      {groups.map((g) => (
        <div className="card" key={g} style={{ marginBottom: '16px' }}>
          <div className="card-header"><h3 className="card-title">{g}</h3></div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <table>
              <thead><tr><th style={{ width: '22%' }}>Activity</th><th>Levels</th><th style={{ width: '110px' }}>Required</th><th style={{ width: '90px' }} /></tr></thead>
              <tbody>
                {flows.filter((f) => f.group === g).map((f) => (
                  <tr key={f.workflow_type}>
                    <td><strong>{f.label}</strong>{f.allow_self_approval && <div className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>Creator may approve</div>}</td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                        {f.levels.map((l, i) => (
                          <React.Fragment key={l.level}>
                            {i > 0 && <span className="text-muted">→</span>}
                            <span style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '3px 8px', fontSize: 'var(--fs-sm)' }}>
                              <strong>L{l.level}</strong> {l.level_label}: {[...l.roles, ...l.user_ids.map((id) => userNames[id] || `user #${id}`)].join(', ')}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    </td>
                    <td>{f.enabled ? <span className="badge badge-success">Yes</span> : <span className="badge badge-secondary">Off</span>}</td>
                    <td><button className="btn btn-sm btn-outline" onClick={() => setEditing(f)}><FaEdit /> Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {editing && (
        <FlowEditor flow={editing} roles={roles} users={users} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};

export default ApprovalFlow;
